import { formatNumber } from '@angular/common';
import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IrdFormattable, IrdValueFormatter } from '@ird/shared-base';
import { TranslateService } from '@ngx-translate/core';
import { merge, scan } from 'rxjs';
import { IrdDatePipe } from '../../pipes/ird-date.pipe';

/**
 * The one value-to-string path for Angular code: `format(value, property)`. The rules are
 * `IrdValueFormatter` in `@ird/shared-base`; this service supplies the locale, the active language
 * and the date pipe to it. Templates use `IrdFormatPipe`, which delegates here.
 */
@Injectable({ providedIn: 'root' })
export class IrdFormatService {
  private readonly locale = inject(LOCALE_ID);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = new IrdDatePipe();
  private readonly formatter = new IrdValueFormatter({
    date: (value, dateFormat) => this.datePipe.transform(value, dateFormat),
    number: (value, digitsInfo) => formatNumber(value, this.locale, digitsInfo),
    translate: (key) => this.translate.instant(key) as string,
  });

  /**
   * Counts language switches and translations arriving late. A template passes it as the last
   * `irdFormat` argument, so the pure pipe re-runs on them; one subscription for the application,
   * not one per cell.
   */
  public readonly translationsVersion = toSignal(
    merge(this.translate.onLangChange, this.translate.onTranslationChange, this.translate.onFallbackLangChange).pipe(
      scan((version) => version + 1, 0),
    ),
    { initialValue: 0 },
  );

  public format(value: unknown, property: IrdFormattable): string {
    return this.formatter.format(value, property);
  }
}
